// NOAA solar position approximation.
//
// Returns sun elevation + azimuth for a given date/time + lat/lng. Good to
// ±1° for latitudes between -60 and +60 at any time of year — plenty for
// our shading visualization (we're not computing actual shadow rays).
//
// Returns:
//   elevation: degrees above the horizon. Negative means sun is down.
//   azimuth:   degrees clockwise from true north (0=N, 90=E, 180=S, 270=W).

export interface SunPosition {
  elevation: number;
  azimuth: number;
  isDaytime: boolean;
}

function toRadians(deg: number): number { return (deg * Math.PI) / 180; }
function toDegrees(rad: number): number { return (rad * 180) / Math.PI; }

/** Days since J2000 epoch (2000-01-01 12:00 UTC). */
function julianDay(date: Date): number {
  return (date.getTime() / 86400000) - 10957.5;
}

export function computeSunPosition(
  date: Date,
  latDeg: number,
  lngDeg: number,
): SunPosition {
  const d = julianDay(date);

  // Mean longitude + anomaly
  const L = (280.460 + 0.9856474 * d) % 360;
  const g = toRadians((357.528 + 0.9856003 * d) % 360);

  // Ecliptic longitude
  const lambda = toRadians(L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g));

  // Obliquity
  const epsilon = toRadians(23.439 - 0.0000004 * d);

  // Right ascension + declination
  const alpha = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda));
  const delta = Math.asin(Math.sin(epsilon) * Math.sin(lambda));

  // Greenwich mean sidereal time (hours)
  const gmst = (18.697374558 + 24.06570982441908 * d) % 24;
  const lmst = (gmst + lngDeg / 15) * 15; // degrees

  // Hour angle
  const ha = toRadians(lmst - toDegrees(alpha));
  const lat = toRadians(latDeg);

  const sinAlt = Math.sin(lat) * Math.sin(delta) + Math.cos(lat) * Math.cos(delta) * Math.cos(ha);
  const altitude = Math.asin(sinAlt);

  const cosAz = (Math.sin(delta) - Math.sin(altitude) * Math.sin(lat)) / (Math.cos(altitude) * Math.cos(lat));
  let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz)));
  if (Math.sin(ha) > 0) azimuth = 2 * Math.PI - azimuth;

  const elevationDeg = toDegrees(altitude);
  const azimuthDeg = (toDegrees(azimuth) + 360) % 360;

  return {
    elevation: Math.round(elevationDeg * 10) / 10,
    azimuth: Math.round(azimuthDeg * 10) / 10,
    isDaytime: elevationDeg > 0,
  };
}
